import React, { useState, useEffect, FormEvent } from 'react';
import { 
  Watch, 
  Coffee, 
  TrendingUp, 
  Users, 
  Target, 
  Award,
  Globe,
  Lightbulb,
  BarChart3,
  Building2,
  CheckCircle,
  ArrowRight,
  Calendar,
  Mail,
  Phone,
  Linkedin,
  ChevronRight,
  Briefcase,
  Code2,
  Brain,
  Send
} from 'lucide-react';
import { toast } from 'react-hot-toast';

// Translations
const translations = {
  en: {
    hero: {
      title: "Strategic Excellence in Business Leadership",
      subtitle: "CFO, COO, and Managing Director Services",
      description: "Proven leadership in executive management with expertise as CFO, COO, and Managing Director for high-growth ventures and established corporations. Successfully transformed operations, optimized financial performance, and drove sustainable growth."
    },
    metrics: {
      yearsExperience: "Years of Excellence",
      yearsCFO: "Years as CFO/COO",
      languages: "Languages Spoken",
      industries: "Industries Mastered"
    },
    services: {
      title: "Strategic Services",
      cfo: {
        title: "CFO & COO Services",
        description: "Executive financial and operational leadership"
      },
      luxury: {
        title: "Luxury Goods & AI",
        description: "Advanced tech integration in luxury markets"
      },
      business: {
        title: "Business Development",
        description: "Strategic growth and market expansion"
      }
    },
    expertise: {
      title: "Areas of Expertise"
    },
    contact: {
      title: "Let's Connect",
      subtitle: "Ready to transform your business? Send me a message and let's discuss how I can help.",
      name: "Your Name",
      email: "Email Address",
      organization: "Organization",
      message: "Your Message",
      placeholder: {
        name: "John Doe",
        email: "john@company.com",
        organization: "Your Company Name",
        message: "Tell me about your project or challenge..."
      },
      required: "Required fields",
      send: "Send Message",
      sending: "Sending...",
      directContact: "Connect on LinkedIn:"
    },
    cta: {
      schedule: "Schedule Consultation",
      contact: "Contact Direct"
    },
    footer: {
      title: "Teacher of Economics & Business Consultant | CFO & COO",
      copyright: "© 2025 Laurent Nuel Consulting. Excellence in Strategic Advisory."
    }
  },
  fr: {
    hero: {
      title: "Excellence Stratégique en Leadership d'Entreprise",
      subtitle: "Services CFO, COO et Directeur Général",
      description: "Leadership prouvé en gestion exécutive avec expertise en tant que CFO, COO et Directeur Général pour entreprises en forte croissance et sociétés établies. Transformation réussie des opérations, optimisation des performances financières et croissance durable."
    },
    metrics: {
      yearsExperience: "Années d'Excellence",
      yearsCFO: "Années CFO/COO",
      languages: "Langues Parlées",
      industries: "Industries Maîtrisées"
    },
    services: {
      title: "Services Stratégiques",
      cfo: {
        title: "Services CFO & COO",
        description: "Leadership financier et opérationnel exécutif"
      },
      luxury: {
        title: "Luxe & IA",
        description: "Intégration technologique avancée dans les marchés du luxe"
      },
      business: {
        title: "Développement d'Affaires",
        description: "Croissance stratégique et expansion de marché"
      }
    },
    expertise: {
      title: "Domaines d'Expertise"
    },
    contact: {
      title: "Prenons Contact",
      subtitle: "Prêt à transformer votre entreprise ? Envoyez-moi un message et discutons de comment je peux vous aider.",
      name: "Votre Nom",
      email: "Adresse Email",
      organization: "Organisation",
      message: "Votre Message",
      placeholder: {
        name: "Jean Dupont",
        email: "jean@entreprise.com",
        organization: "Nom de votre Entreprise",
        message: "Parlez-moi de votre projet ou défi..."
      },
      required: "Champs obligatoires",
      send: "Envoyer le Message",
      sending: "Envoi...",
      directContact: "Connectez-vous sur LinkedIn :"
    },
    cta: {
      schedule: "Planifier une Consultation",
      contact: "Contact Direct"
    },
    footer: {
      title: "Professeur d'Économie & Consultant en Affaires | CFO & COO",
      copyright: "© 2025 Laurent Nuel Consulting. Excellence en Conseil Stratégique."
    }
  },
  es: {
    hero: {
      title: "Excelencia Estratégica en Liderazgo Empresarial",
      subtitle: "Servicios de CFO, COO y Director General",
      description: "Liderazgo probado en gestión ejecutiva con experiencia como CFO, COO y Director General para empresas de alto crecimiento y corporaciones establecidas. Transformación exitosa de operaciones, optimización del rendimiento financiero y crecimiento sostenible."
    },
    metrics: {
      yearsExperience: "Años de Excelencia",
      yearsCFO: "Años como CFO/COO",
      languages: "Idiomas Hablados",
      industries: "Industrias Dominadas"
    },
    services: {
      title: "Servicios Estratégicos",
      cfo: {
        title: "Servicios CFO y COO",
        description: "Liderazgo financiero y operativo ejecutivo"
      },
      luxury: {
        title: "Bienes de Lujo e IA",
        description: "Integración tecnológica avanzada en mercados de lujo"
      },
      business: {
        title: "Desarrollo de Negocios",
        description: "Crecimiento estratégico y expansión de mercado"
      }
    },
    expertise: {
      title: "Áreas de Experiencia"
    },
    contact: {
      title: "Conectemos",
      subtitle: "¿Listo para transformar su negocio? Envíeme un mensaje y discutamos cómo puedo ayudar.",
      name: "Su Nombre",
      email: "Dirección de Email",
      organization: "Organización",
      message: "Su Mensaje",
      placeholder: {
        name: "Juan Pérez",
        email: "juan@empresa.com",
        organization: "Nombre de su Empresa",
        message: "Cuénteme sobre su proyecto o desafío..."
      },
      required: "Campos obligatorios",
      send: "Enviar Mensaje",
      sending: "Enviando...",
      directContact: "Conéctate en LinkedIn:"
    },
    cta: {
      schedule: "Programar Consulta",
      contact: "Contacto Directo"
    },
    footer: {
      title: "Profesor de Economía y Consultor de Negocios | CFO y COO",
      copyright: "© 2025 Laurent Nuel Consulting. Excelencia en Asesoría Estratégica."
    }
  },
  de: {
    hero: {
      title: "Strategische Exzellenz in der Unternehmensführung",
      subtitle: "CFO, COO und Geschäftsführer Dienstleistungen",
      description: "Bewährte Führung im Executive Management mit Expertise als CFO, COO und Geschäftsführer für schnell wachsende Unternehmen und etablierte Konzerne. Erfolgreiche Transformation von Betriebsabläufen, Optimierung der finanziellen Leistung und nachhaltiges Wachstum."
    },
    metrics: {
      yearsExperience: "Jahre der Exzellenz",
      yearsCFO: "Jahre als CFO/COO",
      languages: "Gesprochene Sprachen",
      industries: "Beherrschte Branchen"
    },
    services: {
      title: "Strategische Dienstleistungen",
      cfo: {
        title: "CFO & COO Dienstleistungen",
        description: "Executive Finanz- und Betriebsführung"
      },
      luxury: {
        title: "Luxusgüter & KI",
        description: "Fortschrittliche Technologieintegration in Luxusmärkten"
      },
      business: {
        title: "Geschäftsentwicklung",
        description: "Strategisches Wachstum und Marktexpansion"
      }
    },
    expertise: {
      title: "Fachgebiete"
    },
    contact: {
      title: "Kontaktieren Sie mich",
      subtitle: "Bereit, Ihr Unternehmen zu transformieren? Senden Sie mir eine Nachricht und lassen Sie uns besprechen, wie ich helfen kann.",
      name: "Ihr Name",
      email: "E-Mail-Adresse",
      organization: "Organisation",
      message: "Ihre Nachricht",
      placeholder: {
        name: "Max Mustermann",
        email: "max@unternehmen.com",
        organization: "Ihr Unternehmensname",
        message: "Erzählen Sie mir von Ihrem Projekt oder Ihrer Herausforderung..."
      },
      required: "Pflichtfelder",
      send: "Nachricht senden",
      sending: "Wird gesendet...",
      directContact: "Auf LinkedIn verbinden:"
    },
    cta: {
      schedule: "Beratung vereinbaren",
      contact: "Direkter Kontakt"
    },
    footer: {
      title: "Professor für Wirtschaft & Unternehmensberater | CFO & COO",
      copyright: "© 2025 Laurent Nuel Consulting. Exzellenz in der strategischen Beratung."
    }
  }
};

// Version 2.0 - Optimized UX
const LaurentNuel = () => {
  const [activeService, setActiveService] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [language, setLanguage] = useState<'en' | 'fr' | 'es' | 'de'>('en'); // English by default
  
  // Form states
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formState,
          recipient: 'laurent.nuel@gmail.com' // Spécifier le destinataire
        })
      });

      if (response.ok) {
        toast.success('Message envoyé avec succès ! Laurent vous répondra rapidement.');
        setFormState({ name: '', email: '', organization: '', message: '' });
      } else {
        toast.error('Une erreur est survenue. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Impossible d\'envoyer le message. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Force cache refresh with timestamp
  const cacheVersion = new Date().getTime();
  
  const services = [
    {
      icon: TrendingUp,
      title: t.services.cfo.title,
      description: t.services.cfo.description,
      image: `/images/laurent-nuel/cfo-expertise.png?v=${cacheVersion}`,
      details: [
        "Financial strategy & IFRS consolidation",
        "Supply chain management optimization",
        "Cash flow management & treasury operations",
        "Business restructuring & profitability analysis"
      ]
    },
    {
      icon: Building2,
      title: t.services.luxury.title,
      description: t.services.luxury.description,
      image: `/images/laurent-nuel/luxury-goods.png?v=${cacheVersion}`,
      details: [
        "Pernod Ricard: pricing strategy & portfolio segmentation",
        "Swiss'n Wines: E-commerce & distribution channels",
        "Brand positioning & market penetration",
        "Customer portfolio optimization"
      ]
    },
    {
      icon: Globe,
      title: t.services.business.title,
      description: t.services.business.description,
      image: `/images/laurent-nuel/team-meeting.png?v=${cacheVersion}`,
      details: [
        "Multi-country operations management",
        "Forex exposure & hedging strategies",
        "International compliance & standards",
        "ERP implementation (GL, AP, AR, CB, DTA, BI)"
      ]
    }
  ];

  const metrics = [
    { value: "25+", label: t.metrics.yearsExperience, color: "#0066CC" },
    { value: "10+", label: t.metrics.yearsCFO, color: "#00AA44" },
    { value: "4", label: t.metrics.languages, color: "#FF6600" },
    { value: "3", label: t.metrics.industries, color: "#CC0000" }
  ];

  const expertise = [
    {
      category: "Financial Leadership",
      skills: [
        "Corporate Finance",
        "IFRS Consolidation",
        "Cash Management",
        "Business Planning & Forecasting"
      ]
    },
    {
      category: "Industry Expertise",
      skills: [
        "Luxury Goods (Swiss'n Wines)",
        "FMCG (Pernod Ricard)",
        "Services (SGS)",
        "Wine & Spirits Distribution"
      ]
    },
    {
      category: "Operational Excellence",
      skills: [
        "Supply Chain Management",
        "Business Restructuring",
        "ERP Implementation",
        "Pricing Strategy"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-hidden relative">
      {/* Geometric Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute w-96 h-96 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full opacity-10 blur-3xl"
          style={{ 
            top: '10%', 
            left: '20%',
            transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`
          }}
        />
        <div 
          className="absolute w-96 h-96 bg-gradient-to-br from-cyan-400 to-blue-400 rounded-full opacity-10 blur-3xl"
          style={{ 
            bottom: '20%', 
            right: '10%',
            transform: `translate(${mousePosition.x * -0.02}px, ${mousePosition.y * -0.02}px)`
          }}
        />
        <div className="absolute top-0 left-0 w-full h-full">
          <svg className="w-full h-full opacity-5" viewBox="0 0 1440 800">
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#3B82F6" strokeWidth="1"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
      </div>
      {/* Navigation Bar */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-md z-50 shadow-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center shadow-lg transform hover:rotate-12 transition-transform">
                <span className="text-white font-bold text-lg">LN</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Laurent Nuel</h1>
                <p className="text-xs text-gray-600">Strategic Consultant</p>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <button className="text-gray-700 hover:text-blue-600 transition-colors">{t.services.title}</button>
              <button className="text-gray-700 hover:text-blue-600 transition-colors">{t.expertise.title}</button>
              <a href="#contact" 
                className="px-6 py-2 text-white rounded-lg transition-all transform hover:scale-105 shadow-lg"
                style={{ background: 'linear-gradient(to right, #2563eb, #7c3aed)' }}
              >
                {t.cta.schedule}
              </a>
              
              {/* Language Switcher */}
              <div className="flex gap-2 ml-4 border-l pl-4">
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1 rounded-md font-medium text-sm transition-all ${
                    language === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  title="English"
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage('fr')}
                  className={`px-3 py-1 rounded-md font-medium text-sm transition-all ${
                    language === 'fr' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  title="Français"
                >
                  FR
                </button>
                <button
                  onClick={() => setLanguage('de')}
                  className={`px-3 py-1 rounded-md font-medium text-sm transition-all ${
                    language === 'de' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  title="Deutsch"
                >
                  DE
                </button>
                <button
                  onClick={() => setLanguage('es')}
                  className={`px-3 py-1 rounded-md font-medium text-sm transition-all ${
                    language === 'es' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  title="Español"
                >
                  ES
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-full mb-6 shadow-lg">
                <Briefcase className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">25+ Years of Excellence</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {t.hero.title}
              </h1>
              <h2 className="text-2xl text-gray-700 mb-4">
                {t.hero.subtitle}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-normal">
                {t.hero.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  className="px-6 py-3 text-white rounded-lg transition-all transform hover:scale-105 flex items-center shadow-lg"
                  style={{
                    background: 'linear-gradient(to right, #2563eb, #7c3aed)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #1d4ed8, #6d28d9)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #2563eb, #7c3aed)'}
                >
                  Discuss Your Project
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
                <button 
                  className="px-6 py-3 border-2 text-blue-600 rounded-lg transition-all"
                  style={{
                    backgroundColor: '#eff6ff',
                    borderColor: '#2563eb'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                >
                  {language === 'fr' ? 'Voir les Études de Cas' : language === 'es' ? 'Ver Casos de Estudio' : language === 'de' ? 'Fallstudien ansehen' : 'View Case Studies'}
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl blur opacity-30 animate-pulse"></div>
              <div className="relative bg-white rounded-2xl shadow-2xl border-2 border-gray-100 overflow-hidden">
                <img 
                  src="/images/laurent-nuel/ln.png" 
                  alt="Laurent Nuel - Strategic Consultant"
                  className="w-full h-[450px] object-cover object-[center_45%]"
                />
                <div className="p-8">
                <div className="grid grid-cols-2 gap-4">
                  {metrics.map((metric, index) => (
                    <div key={index} className="text-center p-4 rounded-lg hover:bg-gradient-to-br hover:from-blue-50 hover:to-purple-50 transition-all group cursor-pointer">
                      <div className="text-3xl font-bold group-hover:scale-110 transition-transform" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-600 mt-1 group-hover:text-gray-900">{metric.label}</div>
                    </div>
                  ))}
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section with Image */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Executive Excellence</h2>
              <p className="text-lg text-gray-600 mb-4">
                With over 25 years of experience in executive roles, Laurent Nuel brings unparalleled expertise 
                in financial leadership and operational excellence across luxury goods, FMCG, and services sectors.
              </p>
              <p className="text-lg text-gray-600 mb-4">
                As former CFO and COO of prestigious companies including Pernod Ricard and GROUP R, 
                Laurent has consistently delivered profitable growth and strategic transformation.
              </p>
              <p className="text-lg text-gray-600">
                Currently serving as Managing Director of Swiss'n Wines, Laurent continues to drive innovation 
                in the luxury goods market while offering strategic consulting services to select clients.
              </p>
            </div>
            <div className="relative rounded-xl overflow-hidden shadow-2xl">
              <img 
                src={`/images/laurent-nuel/executive-symbol.png?v=${cacheVersion}`}
                alt="Digital Transformation and Financial Excellence"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 px-6 relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-200 to-blue-200 rounded-full opacity-20 blur-3xl" />
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{t.services.title}</h2>
            <p className="text-lg text-gray-600">Delivering excellence across specialized industries</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className={`relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300 border-2 ${
                  activeService === index 
                    ? 'border-blue-600 shadow-2xl scale-105' 
                    : 'bg-white border-gray-200 shadow-lg hover:shadow-xl hover:border-blue-400'
                }`}
                onMouseEnter={() => setActiveService(index)}
              >
                {service.image && (
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className={`p-6 ${activeService === index ? 'bg-gradient-to-br from-blue-600 to-purple-600' : 'bg-white'}`}>
                  <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center mb-4 shadow-lg">
                    <service.icon className={`w-8 h-8 ${activeService === index ? 'text-white' : 'text-blue-600'}`} />
                  </div>
                <h3 className={`text-xl font-bold mb-2 ${
                  activeService === index ? 'text-white' : 'text-gray-900'
                }`}>{service.title}</h3>
                <p className={`mb-4 text-sm ${
                  activeService === index ? 'text-blue-100' : 'text-gray-600'
                }`}>{service.description}</p>
                <ul className="space-y-3">
                  {service.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 flex-shrink-0 ${
                        activeService === index ? 'text-white' : 'text-blue-600'
                      }`} />
                      <span className={activeService === index ? 'text-white' : 'text-gray-700'}>{detail}</span>
                    </li>
                  ))}
                </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Grid */}
      <section className="py-12 px-6 relative">
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-br from-cyan-200 to-blue-200 rounded-full opacity-20 blur-3xl" />
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{t.expertise.title}</h2>
            <p className="text-lg text-gray-600">Comprehensive capabilities across business and technology</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {expertise.map((area, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-blue-400 transition-all shadow-lg hover:shadow-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Brain className="w-5 h-5 mr-2 text-blue-600" />
                  {area.category}
                </h3>
                <div className="space-y-2">
                  {area.skills.map((skill, idx) => (
                    <div key={idx} className="flex items-center p-2 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors">
                      <ChevronRight className="w-4 h-4 text-blue-600 mr-2" />
                      <span className="text-gray-700 text-sm">{skill}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-12 px-6 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Why Choose Laurent Nuel Consulting</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            {[
              { icon: Globe, title: "Global Perspective", desc: "International experience across 15+ countries" },
              { icon: Target, title: "Results-Driven", desc: "98% client satisfaction rate" },
              { icon: Lightbulb, title: "Innovation Focus", desc: "Cutting-edge solutions for complex challenges" },
              { icon: Users, title: "Partnership Approach", desc: "Long-term collaboration, not just consulting" }
            ].map((item, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/20 transition-colors">
                <item.icon className="w-10 h-10 text-white mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                <p className="text-blue-100 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-6">
              {t.contact.title}
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              {t.contact.subtitle}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  {t.contact.name} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formState.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder={t.contact.placeholder.name}
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t.contact.email} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formState.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder={t.contact.placeholder.email}
                />
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                {t.contact.organization}
              </label>
              <input
                type="text"
                id="organization"
                name="organization"
                value={formState.organization}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder={t.contact.placeholder.organization}
              />
            </div>
            
            <div className="mb-8">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                {t.contact.message} *
              </label>
              <textarea
                id="message"
                name="message"
                value={formState.message}
                onChange={handleChange}
                required
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                placeholder={t.contact.placeholder.message}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                * {t.contact.required}
              </p>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-3 rounded-lg font-medium transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  background: isSubmitting ? '#9CA3AF' : 'linear-gradient(to right, #2563eb, #7c3aed)',
                  color: 'white'
                }}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t.contact.sending}
                  </>
                ) : (
                  <>
                    <Send className="mr-2 w-5 h-5" />
                    {t.contact.send}
                  </>
                )}
              </button>
            </div>
          </form>
          
          <div className="mt-8 text-center">
            <p className="text-blue-100 mb-4">{t.contact.directContact}</p>
            <div className="flex justify-center">
              <a 
                href="https://www.linkedin.com/in/laurentnuel/" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-blue-200 transition-colors flex items-center"
              >
                <Linkedin className="w-5 h-5 mr-2" />
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Laurent Nuel</h3>
              <p className="text-gray-400">{t.footer.title}</p>
            </div>
            <div className="flex space-x-6">
              <a href="https://linkedin.com/in/laurentnuel" className="text-gray-400 hover:text-white transition-colors">
                <Linkedin className="w-6 h-6" />
              </a>
              <a href="https://linkedin.com/in/laurentnuel" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                <Mail className="w-6 h-6" />
              </a>
              <a href="tel:+41000000000" className="text-gray-400 hover:text-white transition-colors">
                <Phone className="w-6 h-6" />
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-400">
            <p>{t.footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LaurentNuel;